/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import flatpickr from "flatpickr";
import rangePlugin from "flatpickr/dist/plugins/rangePlugin";
import autoComplete from "@tarekraafat/autocomplete.js/dist/autoComplete.js";
import domToImage from "dom-to-image";
export class DiscoveryInputComponent {
    constructor() {
        this.defOptions = new Param();
        this.innerOptions = new Param();
        this.disabled = false;
        this.handleClick = () => {
            var _a;
            if (this.inputField &&
                this.subType !== 'date' &&
                this.subType !== 'date-range' &&
                this.subType !== 'multi' &&
                this.subType !== 'chips' &&
                this.subType !== 'chips-autocomplete') {
                if ('value' in this.inputField) {
                    this.selectedValue = this.inputField.value;
                }
            }
            (((_a = this.innerResult) === null || _a === void 0 ? void 0 : _a.events) || []).forEach(e => {
                var _a;
                if (this.selectedValue !== undefined) {
                    if (this.subType === 'date-range' && this.selectedValue.length !== 2) {
                        return;
                    }
                    if (e.selector) {
                        if (!e.value) {
                            e.value = {};
                        }
                        e.value[e.selector] = this.selectedValue;
                    }
                    else {
                        e.value = this.selectedValue;
                    }
                    (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['handleClick', 'emit'], { discoveryEvent: e, subtype: this.subType }, this.selectedValue);
                    this.discoveryEvent.emit(Object.assign(Object.assign({}, e), { source: this.el.id }));
                }
            });
        };
        this.result = undefined;
        this.type = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.url = undefined;
        this.parsing = false;
        this.rendering = false;
        this.value = '';
        this.subType = 'text';
        this.innerStyle = undefined;
        this.innerResult = undefined;
        this.label = 'Ok';
        this.selectedValue = undefined;
        this.values = [];
    }
    discoveryEventHandler(event) {
        const res = Utils.parseEventData(event.detail, this.innerOptions.eventHandler, this.el.id);
        if (res.style) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), res.style);
        }
    }
    updateRes() {
        this.LOG.debug(['updateRes'], this.innerResult);
        this.innerResult = GTSLib.getData(this.result);
        this.innerOptions = Utils.mergeDeep(this.innerOptions || {}, this.innerResult.globalParams);
        if (this.innerOptions.customStyles) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), this.innerOptions.customStyles || {});
        }
        this.parseResult();
    }
    async resize() {
        // empty
    }
    // noinspection JSUnusedLocalSymbols
    async export(type = 'png') {
        const dims = Utils.getContentBounds(this.type === 'input:multi-cb' || this.type === 'input:slider'
            ? this.pngWrapper : this.root);
        this.width = dims.w - 15;
        this.height = dims.h;
        let bgColor = Utils.getCSSColor(this.el, '--warp-view-bg-color', 'transparent');
        bgColor = ((this.innerOptions) || { bgColor }).bgColor || bgColor;
        const dm = ((this.result || {
            globalParams: { bgColor }
        }).globalParams || { bgColor });
        bgColor = dm.bgColor || bgColor;
        if (this.type === 'input:slider') {
            return await this.inputField.export(type, bgColor);
        }
        else {
            return await domToImage.toPng(this.type === 'input:multi-cb' ? this.pngWrapper : this.root, {
                height: this.height,
                width: this.width,
                bgcolor: bgColor
            });
        }
    }
    // noinspection JSUnusedGlobalSymbols
    componentWillLoad() {
        var _a;
        this.LOG = new Logger(DiscoveryInputComponent, this.debug);
        this.parsing = true;
        if (typeof this.options === 'string') {
            this.options = JSON.parse(this.options);
        }
        this.innerResult = GTSLib.getData(this.result);
        this.subType = this.type.split(':')[1];
        let options = Utils.mergeDeep(this.defOptions, this.options || {});
        options = Utils.mergeDeep(options || {}, this.innerResult.globalParams);
        this.innerOptions = Object.assign({}, options);
        const btnLabel = (this.innerOptions.button || { label: 'Ok' }).label;
        const dm = (this.result || {
            globalParams: {
                button: { label: btnLabel }
            }
        }).globalParams || { button: { label: btnLabel } };
        this.label = (dm.button || { label: btnLabel }).label;
        if (this.innerOptions.customStyles) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), this.innerOptions.customStyles || {});
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            innerOptions: this.innerOptions,
            innerResult: this.innerResult,
            result: this.result
        });
    }
    // noinspection JSUnusedGlobalSymbols
    componentDidLoad() {
        var _a;
        switch (this.subType) {
            case 'date':
            case 'date-range':
                const divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
                const opts = {
                    enableTime: true,
                    //    appendTo: this.root,
                    positionElement: this.inputField,
                    static: true,
                    enableSeconds: true,
                    time_24hr: true,
                    plugins: [],
                    formatDate: (d) => GTSLib.toISOString(GTSLib.zonedTimeToUtc(d.valueOf(), 1) * divider, divider, this.innerOptions.timeZone, this.innerOptions.fullDateDisplay ? this.innerOptions.timeFormat : undefined)
                };
                if (this.subType === 'date-range') {
                    opts.plugins = [rangePlugin({ input: this.inputField2 })];
                }
                if (!!this.innerOptions.input && !!this.innerOptions.input.min) {
                    opts.minDate = GTSLib.toISOString(this.innerOptions.input.min, divider, this.innerOptions.timeZone, this.innerOptions.fullDateDisplay ? this.innerOptions.timeFormat : undefined);
                }
                if (!!this.innerOptions.input && !!this.innerOptions.input.max) {
                    opts.maxDate = GTSLib.toISOString(this.innerOptions.input.max, divider, this.innerOptions.timeZone, this.innerOptions.fullDateDisplay ? this.innerOptions.timeFormat : undefined);
                }
                this.flatpickrInstance = flatpickr(this.inputField, opts);
                this.flatpickrInstance.config.onClose.push(() => {
                    var _a;
                    if (this.subType === 'date-range') {
                        this.selectedValue = this.flatpickrInstance.selectedDates.map(date => GTSLib.zonedTimeToUtc(date.valueOf(), 1) * divider);
                    }
                    else {
                        this.selectedValue = GTSLib.zonedTimeToUtc(this.flatpickrInstance.selectedDates[0].valueOf(), 1) * divider;
                    }
                    if (!((_a = this.innerOptions.input) === null || _a === void 0 ? void 0 : _a.showButton)) {
                        this.handleClick();
                    }
                });
                break;
            case 'autocomplete':
                // noinspection JSPotentiallyInvalidConstructorUsage
                this.autoCompleteJS = new autoComplete({
                    placeHolder: 'Search...',
                    selector: () => this.inputField,
                    data: { src: this.values, keys: 'v' },
                    resultItem: { highlight: { render: true } },
                    events: {
                        input: {
                            selection: (event) => {
                                var _a, _b, _c;
                                const selection = event.detail.selection.value.v;
                                this.autoCompleteJS.input.value = selection;
                                this.selectedValue = selection;
                                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['selection'], { v: this.selectedValue, b: !((_b = this.innerOptions.input) === null || _b === void 0 ? void 0 : _b.showButton) });
                                if (!((_c = this.innerOptions.input) === null || _c === void 0 ? void 0 : _c.showButton)) {
                                    this.handleClick();
                                }
                            }
                        }
                    }
                });
                break;
            default:
                break;
        }
        this.parseResult();
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentDidLoad'], {
            type: this.type,
            innerOptions: this.innerOptions,
            innerResult: this.innerResult,
            result: this.result
        });
        this.draw.emit();
    }
    generateStyle(innerStyle) {
        return Object.keys(innerStyle || {}).map(k => k + ' { ' + innerStyle[k] + ' }').join('\n');
    }
    handleSelect(e) {
        var _a, _b, _c;
        this.selectedValue = (_a = e.target.value) !== null && _a !== void 0 ? _a : e.detail;
        if (this.subType === 'chips-autocomplete' || this.subType === 'chips') {
            this.selectedValue = e.detail;
        }
        if (this.subType === 'multi' && ((_b = e.target) === null || _b === void 0 ? void 0 : _b.options)) {
            this.selectedValue = Array.from(e.target.options)
                .filter((o) => !!o.selected)
                .map((o) => o.value);
        }
        if (this.subType === 'multi-cb' && this.checkBoxes) {
            this.selectedValue = Array.from(this.checkBoxes.querySelectorAll('input[type="checkbox"]'))
                .filter((o) => o.checked)
                .map((o) => o.value);
        }
        if (!((_c = this.innerOptions.input) === null || _c === void 0 ? void 0 : _c.showButton)) {
            this.handleClick();
        }
    }
    parseResult() {
        var _a, _b;
        const data = this.innerResult.data || '';
        this.LOG.debug(['parseResult', 'innerOptions'], this.innerOptions);
        switch (this.subType) {
            case 'text':
            case 'secret':
                if (GTSLib.isArray(data) && !!data[0]) {
                    this.value = data[0].toString();
                }
                else {
                    this.value = data.toString();
                }
                this.selectedValue = this.value;
                break;
            case 'date':
                if (GTSLib.isArray(data) && !!data[0]) {
                    this.value = data[0].toString();
                }
                else {
                    this.value = data.toString();
                }
                this.selectedValue = this.value;
                if (this.flatpickrInstance) {
                    this.flatpickrInstance.set('plugins', []);
                    this.flatpickrInstance.setDate(this.formatDateTime(`${this.value}`), true);
                }
                break;
            case 'date-range':
                if (GTSLib.isArray(data) && data.length >= 2) {
                    this.value = data.sort();
                }
                this.selectedValue = this.value;
                if (this.flatpickrInstance) {
                    this.flatpickrInstance.setDate([
                        this.formatDateTime(`${this.value[0]}`),
                        this.formatDateTime(`${this.value[1]}`)
                    ], true);
                }
                break;
            case 'slider':
                this.innerOptions.input = (_a = this.innerOptions.input) !== null && _a !== void 0 ? _a : {};
                this.innerOptions.input.value = (_b = this.innerOptions.input.value) !== null && _b !== void 0 ? _b : data;
                this.value = this.innerOptions.input.value;
                this.selectedValue = this.value;
                this.innerOptions = Object.assign({}, this.innerOptions);
                break;
            case 'list':
            case 'multi':
            case 'multi-cb':
            case 'autocomplete':
            case 'chips':
            case 'chips-autocomplete':
                if (this.checkBoxes) {
                    Array.from(this.checkBoxes.querySelectorAll('input[type="checkbox"]'))
                        .filter((o) => o.checked)
                        .forEach((o) => o.checked = false);
                }
                this.values = [];
                if (GTSLib.isArray(data)) {
                    this.values = [...data];
                }
                else {
                    this.values = [data.toString()];
                }
                if (typeof this.values[0] === 'string' || typeof this.values[0] === 'number') {
                    this.values = this.values.map(s => ({ k: s, v: s, h: false }));
                }
                let index = 0;
                if (!!(this.innerOptions.input || {}).value) {
                    index = this.values.map(o => o.v).indexOf((this.innerOptions.input || {}).value);
                }
                if (!!this.inputField) {
                    this.inputField.selectedIndex = index;
                }
                setTimeout(() => {
                    var _a, _b;
                    let value = (_b = (_a = this.innerOptions) === null || _a === void 0 ? void 0 : _a.input) === null || _b === void 0 ? void 0 : _b.value;
                    if (this.subType === 'multi' || this.subType === 'multi-cb' || this.subType === 'chips' || this.subType === 'chips-autocomplete') {
                        value = value || [];
                        if (!GTSLib.isArray(value)) {
                            value = [value];
                        }
                    }
                    this.value = value;
                    this.selectedValue = this.value;
                });
                if (this.subType === 'autocomplete' && this.autoCompleteJS) {
                    this.autoCompleteJS.data = {
                        src: this.values,
                        keys: 'v',
                        filter: (list) => list.filter(item => {
                            if ('value' in this.inputField) {
                                const inputValue = this.inputField.value.toLowerCase();
                                const itemValue = item.value.v.toLowerCase();
                                if (itemValue.startsWith(inputValue)) {
                                    return item.value;
                                }
                            }
                        })
                    };
                }
                break;
            default:
                return '';
        }
    }
    formatDateTime(timestamp) {
        const divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
        return GTSLib.toISOString(parseInt(timestamp, 10), divider, this.innerOptions.timeZone, undefined).replace('Z', '');
    }
    selectAll(e) {
        if (this.type === 'input:multi-cb' && this.checkBoxes) {
            Array.from(this.checkBoxes.querySelectorAll('input[type="checkbox"]'))
                .filter((o) => !o.checked)
                .forEach((o) => o.checked = true);
            this.handleSelect(e);
        }
    }
    selectNone(e) {
        if (this.type === 'input:multi-cb' && this.checkBoxes) {
            Array.from(this.checkBoxes.querySelectorAll('input[type="checkbox"]'))
                .filter((o) => o.checked)
                .forEach((o) => o.checked = false);
            this.handleSelect(e);
        }
    }
    handleFilter(e) {
        e.stopPropagation();
        if (this.type === 'input:multi-cb' && this.checkBoxes) {
            this.values = this.values.map(v => (Object.assign(Object.assign({}, v), { h: !new RegExp(`.*${(e.target.value || e.detail || '')}.*`, 'gi').test(v.v) })));
        }
    }
    handleAutoComplete(input) {
        if (this.subType === 'chips-autocomplete') {
            return this.values
                .filter(v => v.k.match(new RegExp('^.*' + input + '.*$')))
                .map(v => {
                return v.k;
            });
        }
        else {
            return [];
        }
    }
    getInput() {
        var _a, _b;
        switch (this.subType) {
            case 'text':
                return h("input", { type: "text", class: "discovery-input", value: this.value, onInput: e => this.handleSelect(e), ref: el => this.inputField = el });
            case 'secret':
                return h("input", { type: "password", class: "discovery-input", value: this.value, onInput: e => this.handleSelect(e), ref: el => this.inputField = el });
            case 'date':
                return h("input", { type: "text", class: "discovery-input", ref: el => this.inputField = el });
            case 'date-range':
                return h("div", { class: "range" }, h("span", null, "from"), h("input", { type: "text", class: "discovery-input", ref: el => this.inputField = el }), h("span", null, "to"), h("input", { type: "text", class: "discovery-input", ref: el => this.inputField2 = el }));
            case 'autocomplete':
                return h("input", { type: "text", class: "discovery-input", value: this.value, ref: el => this.inputField = el });
            case 'slider':
                return h("div", { class: "slider-wrapper", ref: el => this.pngWrapper = el }, h("discovery-slider", { options: this.innerOptions, onValueChanged: e => this.handleSelect(e), debug: this.debug, ref: el => this.inputField = el }));
            case 'list':
                return h("select", { class: "discovery-input", onInput: e => this.handleSelect(e), ref: el => this.inputField = el }, this.values.map(v => (h("option", { value: v.k, selected: this.value === v.k }, v.v))));
            case 'multi':
                return h("select", { class: "discovery-input", onInput: e => this.handleSelect(e), multiple: true, ref: el => this.inputField = el }, this.values.map(v => (h("option", { value: v.k, selected: (this.value || []).includes(v.k) }, v.v))));
            case 'multi-cb':
                return h("div", { class: "multi-cb-wrapper", ref: el => this.pngWrapper = el }, h("div", { class: "multi-cb-layout" }, ((_a = this.innerOptions.input) === null || _a === void 0 ? void 0 : _a.showFilter)
                    ? h("input", { type: "text", class: "discovery-input", onKeyUp: e => this.handleFilter(e) })
                    : '', h("div", { class: "multi-cb-list-wrapper", ref: el => this.checkBoxes = el }, this.values.map(v => (h("div", { class: { 'multi-cb-item-wrapper': true, hidden: v.h } }, h("input", { type: "checkbox", value: v.k, checked: (this.value || []).includes(v.k), onInput: e => this.handleSelect(e), name: v.v }), h("label", { htmlFor: v.v }, v.v)))))), h("div", { class: "multi-cb-buttons-wrapper" }, h("div", null, h("button", { class: "discovery-btn secondary", type: "button", onClick: e => this.selectAll(e) }, "All"), h("button", { class: "discovery-btn secondary", type: "button", onClick: e => this.selectNone(e) }, "None")), ((_b = this.innerOptions.input) === null || _b === void 0 ? void 0 : _b.showButton) ?
                    h("div", { class: "discovery-input-btn-wrapper" }, h("button", { class: "discovery-btn", disabled: this.disabled, type: "button", onClick: this.handleClick }, this.label)) : ''));
            case 'chips':
            case 'chips-autocomplete':
                return h("div", { class: "chips-input-wrapper" }, h("discovery-input-chips", { ref: el => this.inputField = el, chips: this.value, autocomplete: this.handleAutoComplete.bind(this), onChipChange: e => this.handleSelect(e), constrain_input: this.subType === 'chips-autocomplete' }));
            default:
                return '';
        }
    }
    render() {
        var _a;
        return [
            h("style", { key: '4598200429b416345d37926317f55af0118580cd' }, this.generateStyle(this.innerStyle)),
            h("div", { key: '86da67acb236890b8f7e8f23a6a3a8346ed8efed', ref: el => this.root = el }, h("div", { key: 'e5078ee7e93bbbaee3d47b92501d78d29c2914e9', class: 'discovery-input-wrapper type-' + this.subType }, this.getInput(), ((_a = this.innerOptions.input) === null || _a === void 0 ? void 0 : _a.showButton) && this.type !== 'input:multi-cb' ?
                h("div", { class: 'discovery-input-btn-wrapper ' + this.subType }, h("button", { class: "discovery-btn", disabled: this.disabled, type: "button", onClick: this.handleClick }, this.label)) : ''))
        ];
    }
    static get is() { return "discovery-input"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-input.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-input.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "url": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "url",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "parsing": {},
            "rendering": {},
            "value": {},
            "subType": {},
            "innerStyle": {},
            "innerResult": {},
            "label": {},
            "selectedValue": {},
            "values": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "execResult",
                "name": "execResult",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any[]",
                    "resolved": "any[]",
                    "references": {}
                }
            }, {
                "method": "statusError",
                "name": "statusError",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "discoveryEvent",
                "name": "discoveryEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "DiscoveryEvent",
                    "resolved": "DiscoveryEvent",
                    "references": {
                        "DiscoveryEvent": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DiscoveryEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        },
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        },
                        "HTMLDiscoverySliderElement": {
                            "location": "global",
                            "id": "global::HTMLDiscoverySliderElement"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "result",
                "methodName": "updateRes"
            }];
    }
    static get listeners() {
        return [{
                "name": "discoveryEvent",
                "method": "discoveryEventHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=discovery-input.js.map
